import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Spin,
  Button,
  Space,
  Typography,
  Tag,
  Select,
  Descriptions,
  Divider,
  message,
  Popconfirm,
  Modal,
  Input,
  Form,
} from "antd";
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  EditOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  MailOutlined,
  CalendarOutlined,
  TeamOutlined,
  SendOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useParams, useHistory } from "react-router-dom";
import { format } from "date-fns";
import {
  getPulseReportById,
  getPulseReportStatistics,
  getSendHistory,
  activatePulseReport,
  pausePulseReport,
  resumePulseReport,
  sendPulseReportNow,
  sendTestPulseReport,
  getTargetCount,
} from "@api/pulse-reports/pulseReport";
import { StatsOverview, PlanBreakdown, TimelineChart, SendHistory } from "./components";

const { Title, Text } = Typography;
const { Option } = Select;

const PulseReportStats = () => {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pulseReport, setPulseReport] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [sendHistoryData, setSendHistoryData] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [period, setPeriod] = useState("30d");
  const [targetCount, setTargetCount] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [testForm] = Form.useForm();

  const fetchData = async () => {
    try {
      setLoading(true);
      const [reportRes, statsRes, historyRes, targetRes] = await Promise.all([
        getPulseReportById(id),
        getPulseReportStatistics(id, period),
        getSendHistory(id, 1, 10),
        getTargetCount(id),
      ]);

      setPulseReport(reportRes.data?.data);
      setStatistics(statsRes.data?.data);
      setSendHistoryData(historyRes.data?.data || []);
      setHistoryPagination({
        current: 1,
        pageSize: 10,
        total: historyRes.data?.pagination?.total || 0,
      });
      setTargetCount(targetRes.data?.data?.targetCount || 0);
    } catch (error) {
      console.error("Error fetching pulse report data:", error);
      message.error("Failed to load pulse report data");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (page, pageSize) => {
    try {
      setHistoryLoading(true);
      const response = await getSendHistory(id, page, pageSize);
      setSendHistoryData(response.data?.data || []);
      setHistoryPagination({
        current: page,
        pageSize,
        total: response.data?.pagination?.total || 0,
      });
    } catch (error) {
      console.error("Error fetching send history:", error);
      message.error("Failed to load send history");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, period]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    message.success("Data refreshed");
  };

  const handlePeriodChange = (value) => {
    setPeriod(value);
  };

  const handleHistoryPageChange = (page, pageSize) => {
    fetchHistory(page, pageSize);
  };

  const handleActivate = async () => {
    try {
      setActionLoading(true);
      await activatePulseReport(id);
      message.success("Pulse report activated");
      await fetchData();
    } catch (error) {
      console.error("Error activating:", error);
      message.error("Failed to activate pulse report");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = async () => {
    try {
      setActionLoading(true);
      await pausePulseReport(id);
      message.success("Pulse report paused");
      await fetchData();
    } catch (error) {
      console.error("Error pausing:", error);
      message.error("Failed to pause pulse report");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    try {
      setActionLoading(true);
      await resumePulseReport(id);
      message.success("Pulse report resumed");
      await fetchData();
    } catch (error) {
      console.error("Error resuming:", error);
      message.error("Failed to resume pulse report");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendNow = async () => {
    try {
      setActionLoading(true);
      await sendPulseReportNow(id);
      message.success("Pulse report send started");
      await fetchData();
    } catch (error) {
      console.error("Error sending:", error);
      message.error("Failed to send pulse report");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendTest = async (values) => {
    try {
      setActionLoading(true);
      const emails = values.testEmails.split(",").map((e) => e.trim()).filter(Boolean);
      await sendTestPulseReport(id, { testEmails: emails, useUserId: values.useUserId });
      message.success("Test email sent");
      setTestModalVisible(false);
      testForm.resetFields();
    } catch (error) {
      console.error("Error sending test:", error);
      message.error("Failed to send test email");
    } finally {
      setActionLoading(false);
    }
  };

  const goBack = () => {
    history.push("/super-admin/pulse-reports");
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      draft: { color: "default", label: "Draft" },
      active: { color: "success", label: "Active" },
      paused: { color: "warning", label: "Paused" },
      archived: { color: "default", label: "Archived" },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const getScheduleLabel = (schedule) => {
    if (!schedule) return "Not configured";
    const typeLabels = {
      on_refill: "On Subscription Refill",
      weekly: `Weekly (${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][schedule.dayOfWeek || 0]})`,
      monthly: `Monthly (Day ${schedule.dayOfMonth || 1})`,
      quarterly: "Quarterly",
      manual: "Manual Only",
    };
    return typeLabels[schedule.type] || schedule.type;
  };

  if (loading) {
    return (
      <Card style={{ textAlign: "center", padding: 50 }}>
        <Spin size="large" />
        <p>Loading pulse report data...</p>
      </Card>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={goBack}
          style={{
            display: "flex",
            alignItems: "center",
            height: 40,
            paddingLeft: 12,
            paddingRight: 16,
          }}
        >
          Back to Pulse Reports
        </Button>

        <Space size="middle" wrap>
          <Select
            value={period}
            onChange={handlePeriodChange}
            style={{ width: 130 }}
            size="middle"
          >
            <Option value="7d">Last 7 days</Option>
            <Option value="30d">Last 30 days</Option>
            <Option value="90d">Last 90 days</Option>
            <Option value="all">All time</Option>
          </Select>
          <Button
            icon={<ReloadOutlined spin={refreshing} />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => history.push(`/super-admin/pulse-reports/${id}/edit`)}
          >
            Edit
          </Button>
          {pulseReport?.status === "draft" && (
            <Popconfirm
              title="Activate this pulse report?"
              description="Once activated, it will start sending according to the schedule."
              onConfirm={handleActivate}
            >
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                loading={actionLoading}
              >
                Activate
              </Button>
            </Popconfirm>
          )}
          {pulseReport?.status === "active" && (
            <Button
              icon={<PauseCircleOutlined />}
              onClick={handlePause}
              loading={actionLoading}
            >
              Pause
            </Button>
          )}
          {pulseReport?.status === "paused" && (
            <Button
              icon={<PlayCircleOutlined />}
              onClick={handleResume}
              loading={actionLoading}
            >
              Resume
            </Button>
          )}
          <Button icon={<MailOutlined />} onClick={() => setTestModalVisible(true)}>
            Send Test
          </Button>
          {(pulseReport?.status === "active" || pulseReport?.status === "paused") && (
            <Popconfirm
              title="Send pulse report now?"
              description={`This will send to ${targetCount?.toLocaleString() || 0} targeted users.`}
              onConfirm={handleSendNow}
            >
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={actionLoading}
                style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
              >
                Send Now
              </Button>
            </Popconfirm>
          )}
        </Space>
      </div>

      {/* Title and Status */}
      <Card bordered={false} style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
          <Col>
            <Title level={3} style={{ marginBottom: 4 }}>
              <MailOutlined style={{ marginRight: 8, color: "#1890ff" }} />
              {pulseReport?.name || "Pulse Report"}
            </Title>
            {pulseReport?.description && (
              <Text type="secondary">{pulseReport?.description}</Text>
            )}
          </Col>
          <Col>
            {getStatusTag(pulseReport?.status)}
          </Col>
        </Row>

        <Row gutter={[24, 16]}>
          <Col xs={12} sm={6} md={4}>
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Report Type</Text>
            </div>
            <Tag color="blue" style={{ textTransform: "capitalize" }}>
              {pulseReport?.reportType}
            </Tag>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Schedule</Text>
            </div>
            <Text>
              <CalendarOutlined style={{ marginRight: 4, color: "#8c8c8c" }} />
              {getScheduleLabel(pulseReport?.schedule)}
            </Text>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Target Audience</Text>
            </div>
            <Text>
              <TeamOutlined style={{ marginRight: 4, color: "#8c8c8c" }} />
              {pulseReport?.targetType === "all"
                ? "All Users"
                : pulseReport?.targetType === "planBased"
                ? `${pulseReport?.includedPlans?.length || 0} Plans`
                : pulseReport?.targetType}
            </Text>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Target Count</Text>
            </div>
            <Text>
              <UserOutlined style={{ marginRight: 4, color: "#8c8c8c" }} />
              {targetCount?.toLocaleString() || 0} users
            </Text>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Total Runs</Text>
            </div>
            <Text strong>{pulseReport?.statistics?.runCount || 0}</Text>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Created</Text>
            </div>
            <Text>
              {pulseReport?.createdAt
                ? format(new Date(pulseReport.createdAt), "PP")
                : "-"}
            </Text>
          </Col>
        </Row>

        <Divider style={{ margin: "16px 0" }} />

        <Row gutter={[24, 8]}>
          <Col xs={24} sm={12}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Text type="secondary" style={{ fontSize: 12, minWidth: 60 }}>Last Run:</Text>
              <Text>
                {pulseReport?.statistics?.lastRunAt
                  ? format(new Date(pulseReport.statistics.lastRunAt), "PPpp")
                  : "Never"}
              </Text>
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Text type="secondary" style={{ fontSize: 12, minWidth: 60 }}>Next Run:</Text>
              <Text>
                {pulseReport?.statistics?.nextRunAt
                  ? format(new Date(pulseReport.statistics.nextRunAt), "PPpp")
                  : "Not scheduled"}
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Statistics Overview */}
      <div style={{ marginBottom: 24 }}>
        <StatsOverview statistics={statistics?.overview} />
      </div>

      {/* Charts Row */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
          <TimelineChart timeline={statistics?.timeline} />
        </Col>
        <Col xs={24} lg={8}>
          <PlanBreakdown byPlan={statistics?.byPlan} />
        </Col>
      </Row>

      {/* Send History */}
      <SendHistory
        history={sendHistoryData}
        loading={historyLoading}
        pagination={historyPagination}
        onPageChange={handleHistoryPageChange}
      />

      {/* Test Email Modal */}
      <Modal
        title="Send Test Email"
        open={testModalVisible}
        onCancel={() => setTestModalVisible(false)}
        footer={null}
      >
        <Form form={testForm} layout="vertical" onFinish={handleSendTest}>
          <Form.Item
            name="testEmails"
            label="Test Email Addresses"
            rules={[{ required: true, message: "Please enter at least one email" }]}
            extra="Enter multiple emails separated by commas"
          >
            <Input.TextArea
              rows={3}
              placeholder="email1@example.com, email2@example.com"
            />
          </Form.Item>
          <Form.Item
            name="useUserId"
            label="Use Data From User (Optional)"
            extra="Enter a user ID to use their actual data in the preview"
          >
            <Input placeholder="User ID (optional)" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => setTestModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={actionLoading}>
                Send Test
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PulseReportStats;
